<template>
    <div class="flex size-full flex-col">
        <div :class="timeRecordingModeColorClass" class="mb-4 flex items-center justify-center rounded-md p-2 text-5xl text-neutral-100 xl:p-4 xl:text-6xl">
            <ion-icon :icon="timeRecordingModeIcon" class="mr-2" size="large" />
            <span class="mr-4">{{ t(`timeRecordingMode.${timeRecordingMode}`) }}</span>
            <current-time-clock />
        </div>

        <div v-if="deviceStore.deviceConfig?.gatherEmployeeFeedback && employeeFeedbackStore.employeeFeedbackQuestionPerAction[timeRecordingMode]" class="flex flex-col items-center gap-1">
            <h3 v-dompurify-html="employeeFeedbackStore.employeeFeedbackQuestionPerAction[timeRecordingMode]?.question.de" class="text-wrap text-3xl"></h3>
            <span
                v-if="employeeFeedbackStore.employeeFeedbackQuestionPerAction[timeRecordingMode]?.question.en"
                v-dompurify-html="employeeFeedbackStore.employeeFeedbackQuestionPerAction[timeRecordingMode]?.question.en"
                class="text-xl"
            />

            <div class="mt-2 flex flex-row items-center justify-center gap-2">
                <employee-feedback-answer
                    v-for="answerOption in answersSortedByRank"
                    :key="answerOption.value"
                    :answer-option="answerOption"
                    :has-answer-been-selected="!!selectedAnswerOption?.value"
                    :selected="selectedAnswerOption?.value === answerOption.value"
                    @click="selectedAnswerOption = answerOption"
                />
            </div>
        </div>

        <div class="flex grow flex-col items-center justify-center gap-2">
            <span v-if="isEmployeeFeedbackQuestionRequiredButNotAnswered" class="rounded-md bg-red-500 px-1 text-white">
                {{ t("employeeFeedback.required") }}
            </span>
            <number-input-with-keyboard
                v-model="pin"
                :code-length="submitThreshold"
                input-type="password"
                :disabled="!isPinEntryReady || isEmployeeFeedbackQuestionRequiredButNotAnswered"
                @cancel="returnToStartPage"
                @update:model-value="restartAutoDismiss"
            />
        </div>

        <successful-recorded-times-modal
            v-model:is-open="isSuccessRecordedTimeModalOpen"
            type="success"
            :recorded-assignments="recordedAssignments"
            :can-change-group="canChangeGroup"
            :groups="availableGroups"
            @click-change-group="
                isGroupSelectionModalOpen = true;
                isSuccessRecordedTimeModalOpen = false;
            "
        />
        <group-selection-modal
            v-if="canChangeGroup && latestRecordedAssignment"
            v-model:is-open="isGroupSelectionModalOpen"
            :pin="pin"
            :recorded-assignment="latestRecordedAssignment"
            :groups="availableGroups"
            @did-change-group="handleGroupChange"
            @did-close="returnToStartPage"
        />
        <record-time-error-modal v-if="error" v-model:is-open="isRecordTimeErrorModalOpen" type="error" :axios-error="error" :time-recording-mode="timeRecordingMode" />
    </div>
</template>

<script lang="ts" setup>
    // Components
    import { IonIcon } from "@ionic/vue";
    import SuccessfulRecordedTimesModal from "@/components/modals/SuccessfulRecordedTimesModal.vue";
    import RecordTimeErrorModal from "@/components/modals/RecordTimeErrorModal.vue";
    import CurrentTimeClock from "@/components/CurrentTimeClock.vue";
    import EmployeeFeedbackAnswer from "@/components/EmployeeFeedbackAnswer.vue";
    import NumberInputWithKeyboard from "../components/NumberInputWithKeyboard.vue";
    import { cafe, home, logIn, logOut } from "ionicons/icons";

    import { useDeviceStore } from "@/stores/device.store";
    import { computed, onActivated, onMounted, ref, watch } from "vue";
    import { RecordTimesAction } from "@/interfaces/record-times-action.enum";
    import { useRoute, useRouter } from "vue-router";
    import { useRecordTime } from "@/services/record-time.service";
    import { AxiosError } from "axios";
    import { BaseGroupDto, EmployeeFeedbackActiveAtAction, EmployeeFeedbackAnswerOptionDto, RecordedAssignmentWithEmployeeNameDto } from "@/services/api/services";
    import { RecordTimeErrorCode } from "@/interfaces/record-time-error-codes.enum";
    import { useI18n } from "vue-i18n";
    import { useConnectionState } from "@/services/connection-state.service";
    import { useAutoDismiss } from "@/composables/useAutoDismiss";
    import { toast } from "vue3-toastify";
    import { useCameraStore } from "@/stores/camera.store";
    import { useEmployeeFeedbackStore } from "@/stores/employee-feedback.store";
    import { EmployeeFeedbackService } from "@/services/employee-feedback.service";
    import GroupSelectionModal from "@/components/modals/GroupSelectionModal.vue";
    import { useGroupService } from "@/services/group.service";

    const { t } = useI18n({ useScope: "global" });
    const deviceStore = useDeviceStore();
    const groupService = useGroupService();
    const recordTimeService = useRecordTime();
    const router = useRouter();
    const route = useRoute();
    const employeeFeedbackStore = useEmployeeFeedbackStore();
    const recordedAssignments = ref<RecordedAssignmentWithEmployeeNameDto[] | undefined>();
    const isSuccessRecordedTimeModalOpen = ref(false);
    const isRecordTimeErrorModalOpen = ref(false);
    const isGroupSelectionModalOpen = ref(false);
    const availableGroups = ref<BaseGroupDto[]>([]);
    const error = ref<AxiosError | undefined>();

    const selectedAnswerOption = ref<EmployeeFeedbackAnswerOptionDto | null>(null);

    const pin = ref("");

    const resetState = () => {
        isSuccessRecordedTimeModalOpen.value = false;
        isRecordTimeErrorModalOpen.value = false;
        isGroupSelectionModalOpen.value = false;
        selectedAnswerOption.value = null;
        error.value = undefined;
        recordedAssignments.value = undefined;
        pin.value = "";
    };

    const returnToStartPage = async () => {
        resetState();
        try {
            await router.replace({ name: "RecordTimes" });
        } catch (error) {
            console.error("Error while replacing router to RecordTimes", JSON.stringify(error));
            // ignore error for now: https://nesto.atlassian.net/browse/NR-9258
        }
    };

    const { restart: restartAutoDismiss, reset: cancelAutoDismiss } = useAutoDismiss(returnToStartPage, { trackProgress: false, defaultDuration: { seconds: 30 } });
    const isSubmitting = ref(false);

    const { hasRecentLastConnection } = useConnectionState();
    const cameraStore = useCameraStore();

    const canChangeGroup = computed(
        () =>
            deviceStore.deviceConfig?.isGroupSelectionAfterClockInEnabled &&
            (timeRecordingMode.value === RecordTimesAction.RECORD_START_TIME || timeRecordingMode.value === RecordTimesAction.RECORD_BREAK_END_TIME),
    );

    const isPinEntryReady = computed(() => {
        if (isSubmitting.value) return false;
        if (!hasRecentLastConnection.value) return false;
        if (shouldTakePicture.value) {
            return cameraStore.isCameraActive;
        }

        return true;
    });

    const isEmployeeFeedbackQuestionRequiredButNotAnswered = computed(() => {
        return deviceStore.deviceConfig?.gatherEmployeeFeedback && employeeFeedbackStore.employeeFeedbackQuestionPerAction[timeRecordingMode.value]?.required && !selectedAnswerOption.value;
    });

    const answersSortedByRank = computed(() => {
        const possibleEmployeeFeedbackAnswers = employeeFeedbackStore.employeeFeedbackQuestionPerAction[timeRecordingMode.value]?.possibleAnswers || [];
        return possibleEmployeeFeedbackAnswers.sort((a, b) => {
            // sort ascending
            return a.rank - b.rank;
        });
    });

    const latestRecordedAssignment = computed(() => {
        if (recordedAssignments.value && recordedAssignments.value.length > 0) {
            return recordedAssignments.value
                .filter((a): a is RecordedAssignmentWithEmployeeNameDto & { updated: Date } => a.updated instanceof Date)
                .sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime())[0];
        }

        return null;
    });

    const timeRecordingMode = computed(() => (router.currentRoute.value.query.mode as RecordTimesAction) || RecordTimesAction.RECORD_START_TIME); // Fall back to some mode to prevent undefined

    const timeRecordingActionForFeedback = computed((): EmployeeFeedbackActiveAtAction => {
        switch (timeRecordingMode.value) {
            case RecordTimesAction.RECORD_END_TIME:
                return EmployeeFeedbackActiveAtAction.recordEndTime;
            case RecordTimesAction.RECORD_START_TIME:
                return EmployeeFeedbackActiveAtAction.recordStartTime;
            case RecordTimesAction.RECORD_BREAK_START_TIME:
                return EmployeeFeedbackActiveAtAction.recordBreakStartTime;
            case RecordTimesAction.RECORD_BREAK_END_TIME:
                return EmployeeFeedbackActiveAtAction.recordEndTime;
            default:
                return EmployeeFeedbackActiveAtAction.recordStartTime; // Fall back to some action
        }
    });

    const submitThreshold = computed(() => {
        return deviceStore.deviceConfig?.pinLength ?? 4;
    });

    const shouldTakePicture = computed(() => {
        switch (timeRecordingMode.value) {
            case RecordTimesAction.RECORD_END_TIME:
            case RecordTimesAction.RECORD_START_TIME:
                return deviceStore.deviceConfig?.takePictureWhenClockingInOrOut ?? false;
            case RecordTimesAction.RECORD_BREAK_END_TIME:
            case RecordTimesAction.RECORD_BREAK_START_TIME:
                return false;
            default:
                return false;
        }
    });

    const timeRecordingModeColorClass = computed(() => {
        if (!isPinEntryReady.value) {
            return "bg-neutral-300";
        }
        switch (timeRecordingMode.value) {
            case RecordTimesAction.RECORD_END_TIME:
                return "bg-warning";
            case RecordTimesAction.RECORD_START_TIME:
                return "bg-primary";
            case RecordTimesAction.RECORD_BREAK_START_TIME:
            case RecordTimesAction.RECORD_BREAK_END_TIME:
                return "bg-secondary";
            default:
                return "";
        }
    });

    const timeRecordingModeIcon = computed(() => {
        switch (timeRecordingMode.value) {
            case RecordTimesAction.RECORD_END_TIME:
                return home;
            case RecordTimesAction.RECORD_START_TIME:
                return logIn;
            case RecordTimesAction.RECORD_BREAK_START_TIME:
                return cafe;
            case RecordTimesAction.RECORD_BREAK_END_TIME:
                return logOut;
            default:
                return logIn; // Fall back to some icon
        }
    });

    watch(
        isSuccessRecordedTimeModalOpen,
        (newValue) => {
            // After successful clock in, return to start page since the user is done inspecting their shift times
            if (!isGroupSelectionModalOpen.value && newValue === false) {
                returnToStartPage();
            }
        },
        { flush: "post" },
    );

    watch(isGroupSelectionModalOpen, (newValue, oldValue) => {
        /* When closing the group selection modal, we return back to the overview.
        This is necessary to show the user the state of their recorded assignments, e.g. for if the group was changed. */
        if (!newValue && oldValue) {
            isSuccessRecordedTimeModalOpen.value = true;
        }
    });

    watch(isRecordTimeErrorModalOpen, (newValue) => {
        if (!newValue) {
            returnToStartPage();
        }
    });

    watch(
        () => route.path,
        () => {
            if (route.path.includes("pinEntry")) restartAutoDismiss();
        },
    );

    // automatically submit when configured pin length is reached
    watch(pin, (newValue) => {
        if (newValue.length === submitThreshold.value) {
            submit();
        }
    });

    onMounted(() => {
        resetState();
        restartAutoDismiss();
        cameraStore.startCamera();
    });

    onActivated(() => {
        resetState();
        restartAutoDismiss();
        cameraStore.startCamera();
    });

    const handleGroupChange = (updatedRecordedAssignment: RecordedAssignmentWithEmployeeNameDto): void => {
        if (!recordedAssignments.value) {
            return;
        }

        const index = recordedAssignments.value.findIndex((a) => a._id === updatedRecordedAssignment._id);
        if (index !== -1) {
            recordedAssignments.value[index] = updatedRecordedAssignment;
        }

        isGroupSelectionModalOpen.value = false;
        isSuccessRecordedTimeModalOpen.value = true;
    };

    const fetchGroups = async (pinCode: string) => {
        if (!deviceStore.deviceConfig?.isGroupSelectionAfterClockInEnabled) {
            return;
        }

        try {
            availableGroups.value = await groupService.getAvailableGroupsForEmployee(pinCode);
        } catch (error) {
            console.error("An error occurred while trying to fetch the structureNode's available groups", JSON.stringify(error));
            toast.error(t("groupSelection.errors.fetchAvailableGroups"));
        }
    };

    const submit = async () => {
        try {
            isSubmitting.value = true;
            cancelAutoDismiss();
            let base64PictureData: string | undefined = undefined;

            // Take picture if required
            if (shouldTakePicture.value) {
                base64PictureData = await cameraStore.takePicture();
            }

            recordedAssignments.value = await recordTimeService.recordTime(timeRecordingMode.value, pin.value, base64PictureData);
            await fetchGroups(pin.value);
            isSuccessRecordedTimeModalOpen.value = true;

            submitEmployeeFeedback();
        } catch (e) {
            const axiosError = e as AxiosError;
            error.value = axiosError;

            const errorCode = (axiosError.response?.data as any)?.code;
            if (errorCode === RecordTimeErrorCode.UNKNOWN_PIN) {
                pin.value = "";
                toast(t("timeRecordingError.invalidPin"), {
                    type: "error",
                });
            } else {
                isRecordTimeErrorModalOpen.value = true;
            }
            restartAutoDismiss();
        } finally {
            isSubmitting.value = false;
        }
    };

    /**
     * Submit employee feedback if configured and a question is available for the current time recording mode.
     */
    async function submitEmployeeFeedback(): Promise<void> {
        // we need to break reactivity here as otherwise the value could be changed while we are trying to save the feedback
        if (employeeFeedbackStore.employeeFeedbackQuestionPerAction[timeRecordingMode.value]) {
            const currentlyShownQuestion = JSON.parse(JSON.stringify(employeeFeedbackStore.employeeFeedbackQuestionPerAction[timeRecordingMode.value]));
            if (deviceStore.deviceConfig?.gatherEmployeeFeedback && currentlyShownQuestion) {
                // find the last changed recorded assignment
                if (latestRecordedAssignment.value) {
                    if (!latestRecordedAssignment.value._id) {
                        console.error("No _id found for latest recorded assignment");
                        return;
                    }

                    try {
                        await EmployeeFeedbackService.getInstance().saveEmployeeFeedback(
                            currentlyShownQuestion._id,
                            latestRecordedAssignment.value._id,
                            selectedAnswerOption.value,
                            timeRecordingActionForFeedback.value,
                        );
                    } catch (error) {
                        console.error("Error while saving employee feedback", error);
                    }
                }
            }
        }
    }
</script>
